import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import clsx from 'clsx';

const mockPromotions = {
  featured: [
    {
      id: 1,
      title: 'Summer Beach Party',
      business: 'Beach Club Paradise',
      businessId: 101,
      description: 'Join us for the ultimate summer beach party with live DJ, drinks, and games!',
      originalPrice: 50,
      promotionalPrice: 30,
      discount: 40,
      validUntil: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      image: '/api/placeholder/300/200',
      category: 'Entertainment',
      featured: true,
      attendees: 45,
      maxAttendees: 100,
    },
    {
      id: 2,
      title: 'Gourmet Cooking Class',
      business: 'Culinary Academy',
      businessId: 102,
      description: 'Learn to cook like a professional chef with our hands-on cooking class',
      originalPrice: 120,
      promotionalPrice: 80,
      discount: 33,
      validUntil: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
      image: '/api/placeholder/300/200',
      category: 'Education',
      featured: true,
      attendees: 8,
      maxAttendees: 12,
    },
  ],
  nearMe: [
    {
      id: 3,
      title: 'Local Art Gallery Opening',
      business: 'Downtown Gallery',
      businessId: 103,
      description: 'Exclusive opening night for new contemporary art exhibition',
      originalPrice: 25,
      promotionalPrice: 15,
      discount: 40,
      validUntil: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
      image: '/api/placeholder/300/200',
      category: 'Arts & Culture',
      distance: '0.8 miles',
      attendees: 20,
      maxAttendees: 50,
    },
    {
      id: 4,
      title: 'Yoga in the Park',
      business: 'Zen Wellness Studio',
      businessId: 104,
      description: 'Morning yoga session in the beautiful Central Park',
      originalPrice: 20,
      promotionalPrice: 10,
      discount: 50,
      validUntil: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
      image: '/api/placeholder/300/200',
      category: 'Health & Wellness',
      distance: '1.2 miles',
      attendees: 15,
      maxAttendees: 25,
    },
  ],
  followedBusinesses: [
    {
      id: 5,
      title: 'Wine Tasting Evening',
      business: 'Vineyard Bistro',
      businessId: 105,
      description: 'Taste premium wines from local vineyards with expert sommeliers',
      originalPrice: 75,
      promotionalPrice: 55,
      discount: 27,
      validUntil: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000),
      image: '/api/placeholder/300/200',
      category: 'Food & Drink',
      isFollowing: true,
      attendees: 12,
      maxAttendees: 20,
    },
  ],
  active: [
    {
      id: 6,
      title: 'Flash Sale: Photography Workshop',
      business: 'Photo Academy',
      businessId: 106,
      description: '24-hour flash sale on our popular photography workshop',
      originalPrice: 100,
      promotionalPrice: 60,
      discount: 40,
      validUntil: new Date(Date.now() + 18 * 60 * 60 * 1000),
      image: '/api/placeholder/300/200',
      category: 'Education',
      isFlashSale: true,
      attendees: 5,
      maxAttendees: 15,
    },
    {
      id: 7,
      title: 'Early Bird: Marathon Training',
      business: 'Fitness First',
      businessId: 107,
      description: 'Early bird pricing for 12-week marathon training program',
      originalPrice: 200,
      promotionalPrice: 150,
      discount: 25,
      validUntil: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
      image: '/api/placeholder/300/200',
      category: 'Sports & Fitness',
      isEarlyBird: true,
      attendees: 8,
      maxAttendees: 30,
    },
  ],
};

const tabConfig = [
  { key: 'featured', label: 'Featured' },
  { key: 'nearMe', label: 'Near Me' },
  { key: 'followedBusinesses', label: 'Following' },
  { key: 'active', label: 'Active Deals' },
];

const emptyMessages = {
  featured: {
    title: 'No featured promotions',
    description: 'Check back later for featured deals.',
  },
  nearMe: {
    title: 'No promotions near you',
    description: 'Try enabling location services to find deals around you.',
  },
  followedBusinesses: {
    title: 'No promotions from followed businesses',
    description: 'Follow more businesses to see their exclusive promotions.',
    action: true,
  },
  active: {
    title: 'No active deals',
    description: 'All deals have expired. New ones coming soon!',
  },
};

// ---- Skeleton card ----
function PromotionCardSkeleton() {
  return (
    <div className="bg-white rounded-2xl shadow-soft-sm border border-surface-100 overflow-hidden">
      <div className="h-48 skeleton" />
      <div className="p-5 space-y-3">
        <div className="h-5 w-3/4 skeleton rounded-lg" />
        <div className="h-4 w-1/2 skeleton rounded-lg" />
        <div className="h-4 w-full skeleton rounded-lg" />
        <div className="h-4 w-5/6 skeleton rounded-lg" />
        <div className="flex justify-between items-center pt-2">
          <div className="h-7 w-24 skeleton rounded-xl" />
          <div className="h-7 w-20 skeleton rounded-xl" />
        </div>
        <div className="h-9 w-full skeleton rounded-xl" />
      </div>
    </div>
  );
}

// ---- Promotion badge ----
function PromotionBadge({ promotion }) {
  if (promotion.isFlashSale) {
    return (
      <span className="inline-flex items-center gap-1 bg-accent-500 text-white px-2.5 py-1 rounded-full text-xs font-bold tracking-wide">
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
        </svg>
        Flash Sale
      </span>
    );
  }
  if (promotion.isEarlyBird) {
    return (
      <span className="inline-flex items-center gap-1 bg-primary-600 text-white px-2.5 py-1 rounded-full text-xs font-bold tracking-wide">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Early Bird
      </span>
    );
  }
  if (promotion.featured) {
    return (
      <span className="inline-flex items-center gap-1 bg-warning-500 text-white px-2.5 py-1 rounded-full text-xs font-bold tracking-wide">
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
        Featured
      </span>
    );
  }
  return null;
}

// ---- Promotion card ----
function PromotionCard({ promotion, index }) {
  const formatTimeRemaining = (validUntil) => {
    const diff = validUntil - new Date();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (days > 0) return `${days}d left`;
    if (hours > 0) return `${hours}h left`;
    if (minutes > 0) return `${minutes}m left`;
    return 'Expiring soon';
  };

  const filledPct = Math.round((promotion.attendees / promotion.maxAttendees) * 100);
  const isAlmostFull = filledPct >= 80;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.07, ease: 'easeOut' }}
      className="bg-white rounded-2xl shadow-soft-sm border border-surface-100 overflow-hidden hover:shadow-soft-lg hover:-translate-y-1 transition-all duration-300 flex flex-col"
    >
      {/* Image */}
      <div className="relative overflow-hidden">
        <img
          src={promotion.image}
          alt={promotion.title}
          className="w-full h-48 object-cover transition-transform duration-500 hover:scale-105"
          loading="lazy"
        />
        <div className="absolute top-3 left-3">
          <PromotionBadge promotion={promotion} />
        </div>
        <div className="absolute top-3 right-3">
          <span className="inline-flex items-center bg-success-500 text-white px-2.5 py-1 rounded-full text-xs font-bold shadow-soft-sm">
            {promotion.discount}% OFF
          </span>
        </div>
        {/* Countdown pill */}
        <div className="absolute bottom-3 right-3">
          <span className={clsx(
            'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold backdrop-blur-soft shadow-soft-sm',
            promotion.isFlashSale
              ? 'bg-accent-500/90 text-white'
              : 'bg-white/90 text-surface-700'
          )}>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {formatTimeRemaining(promotion.validUntil)}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="p-5 flex flex-col flex-1">
        <div className="mb-1">
          <span className="text-caption text-primary-600 font-semibold uppercase tracking-wide">
            {promotion.category}
          </span>
        </div>

        <h3 className="text-h3 text-surface-900 mb-1 line-clamp-2">{promotion.title}</h3>

        <Link
          to={`/business/${promotion.businessId}`}
          className="text-body-sm text-primary-600 hover:text-primary-700 font-medium mb-2 inline-block"
        >
          {promotion.business}
        </Link>

        <p className="text-body-sm text-surface-500 line-clamp-2 mb-4 flex-1">
          {promotion.description}
        </p>

        {/* Attendee progress */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-caption text-surface-500">
              {promotion.attendees}/{promotion.maxAttendees} joined
            </span>
            {isAlmostFull && (
              <span className="text-caption text-accent-600 font-semibold">Almost full!</span>
            )}
          </div>
          <div className="h-1.5 bg-surface-100 rounded-full overflow-hidden">
            <div
              className={clsx(
                'h-full rounded-full transition-all duration-500',
                isAlmostFull ? 'bg-accent-500' : 'bg-primary-500'
              )}
              style={{ width: `${filledPct}%` }}
            />
          </div>
        </div>

        {/* Pricing */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-baseline gap-2">
            <span className="text-h2 font-bold text-success-600">
              ${promotion.promotionalPrice}
            </span>
            <span className="text-body text-surface-400 line-through">
              ${promotion.originalPrice}
            </span>
          </div>
          {promotion.distance && (
            <span className="inline-flex items-center gap-1 text-caption text-surface-500 bg-surface-100 px-2 py-1 rounded-lg">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {promotion.distance}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-auto">
          <Link
            to={`/events/${promotion.id}`}
            className="flex-1 bg-primary-500 hover:bg-primary-600 text-white text-center py-2.5 px-4 rounded-xl text-body-sm font-semibold transition-colors duration-200"
          >
            View Details
          </Link>
          <button className="bg-success-500 hover:bg-success-600 text-white py-2.5 px-4 rounded-xl text-body-sm font-semibold transition-colors duration-200">
            Join
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ---- Empty state for promotions ----
function PromotionsEmptyState({ activeTab }) {
  const msg = emptyMessages[activeTab] || emptyMessages.featured;
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center px-6">
      <div className="w-16 h-16 rounded-2xl bg-primary-50 text-primary-400 flex items-center justify-center mb-5">
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
        </svg>
      </div>
      <h3 className="text-h3 text-surface-900 mb-2">{msg.title}</h3>
      <p className="text-body text-surface-500 max-w-sm mb-6">{msg.description}</p>
      {msg.action && (
        <Link
          to="/social"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-body-sm font-semibold rounded-xl hover:bg-primary-600 transition-colors duration-200"
        >
          Find Businesses to Follow
        </Link>
      )}
    </div>
  );
}

// ---- Main page ----
const PromotionPage = () => {
  const [promotions, setPromotions] = useState({
    featured: [],
    nearMe: [],
    followedBusinesses: [],
    active: [],
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('featured');

  useEffect(() => {
    fetchPromotions();
    getCurrentLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPromotions = async () => {
    setLoading(true);
    try {
      // Replace with actual API call when available
      setPromotions(mockPromotions);
    } catch (err) {
      toast.error('Failed to load promotions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      () => {
        // Location acquired — could be used to sort nearMe results
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          toast.warning('Location access denied. Showing all nearby promotions without distance sorting.');
        } else {
          toast.error('Could not determine your location. Nearby promotions may be inaccurate.');
        }
      }
    );
  };

  const currentList = promotions[activeTab] || [];

  return (
    <div className="max-w-7xl mx-auto font-sans">
      {/* Page header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-8"
      >
        <h1 className="text-h1 text-surface-900 mb-2">Promotions</h1>
        <p className="text-body-lg text-surface-500">
          Discover amazing deals and discounts on CliQQ events from your favourite businesses.
        </p>
      </motion.div>

      {/* Tab bar */}
      <div className="relative flex border-b border-surface-200 mb-8 gap-1 overflow-x-auto">
        {tabConfig.map(({ key, label }) => {
          const count = promotions[key]?.length ?? 0;
          const isActive = activeTab === key;
          return (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={clsx(
                'relative flex items-center gap-2 px-4 py-3 text-body-sm font-semibold whitespace-nowrap transition-colors duration-200 outline-none',
                'focus-visible:ring-2 focus-visible:ring-primary-500/40 rounded-t-lg',
                isActive ? 'text-primary-600' : 'text-surface-500 hover:text-surface-800'
              )}
            >
              {label}
              {count > 0 && (
                <span className={clsx(
                  'px-1.5 py-0.5 text-[11px] font-bold rounded-full',
                  isActive ? 'bg-primary-100 text-primary-600' : 'bg-surface-100 text-surface-500'
                )}>
                  {count}
                </span>
              )}
              {isActive && (
                <motion.div
                  layoutId="promo-tab-indicator"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500 rounded-full"
                  transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Grid */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => <PromotionCardSkeleton key={i} />)}
            </div>
          ) : currentList.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentList.map((promotion, i) => (
                <PromotionCard key={promotion.id} promotion={promotion} index={i} />
              ))}
            </div>
          ) : (
            <PromotionsEmptyState activeTab={activeTab} />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Promotional banner */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="mt-14 bg-gradient-to-r from-primary-600 to-primary-800 rounded-2xl p-8 md:p-10 text-white overflow-hidden relative"
      >
        {/* Decorative circles */}
        <div className="absolute -top-10 -right-10 w-48 h-48 bg-white/5 rounded-full pointer-events-none" aria-hidden="true" />
        <div className="absolute -bottom-8 right-24 w-32 h-32 bg-white/5 rounded-full pointer-events-none" aria-hidden="true" />

        <div className="relative max-w-2xl">
          <h2 className="text-h2 font-bold mb-3">Want to promote your events?</h2>
          <p className="text-primary-100 text-body-lg mb-7 leading-relaxed">
            Reach thousands of potential attendees with targeted promotions.
            Boost your event visibility and increase attendance with our promotion tools.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/business/promotions"
              className="bg-white text-primary-700 px-6 py-2.5 rounded-xl font-semibold text-body-sm hover:bg-primary-50 transition-colors duration-200"
            >
              Create Promotion
            </Link>
            <Link
              to="/business/analytics"
              className="border border-white/50 text-white px-6 py-2.5 rounded-xl font-semibold text-body-sm hover:bg-white/10 transition-colors duration-200"
            >
              View Analytics
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PromotionPage;
